import { createSlice } from "@reduxjs/toolkit";

import {
  getDirectContacts,
  getDirectConversation,
  sendDirectMessage,
} from "./thunk";
import { CommunicationState, DirectMessage } from "./types";

const initialState: CommunicationState = {
  contacts: [],
  activeUserId: null,
  messages: [],
  loadingContacts: false,
  loadingMessages: false,
  error: null,
};

const communicationSlice = createSlice({
  name: "communication",
  initialState,
  reducers: {
    setActiveUser: (state, action) => {
      state.activeUserId = action.payload;
    },
    // Incoming message pushed from the server via SignalR (always from another user).
    receiveDirectMessage: (state, action) => {
      const message: DirectMessage = action.payload;
      if (!message) return;

      const isActiveThread = state.activeUserId === message.senderId;
      if (isActiveThread && !state.messages.some((m) => m.id === message.id)) {
        state.messages.push({ ...message, isMine: false });
      }

      const contact = state.contacts.find((c) => c.userId === message.senderId);
      if (contact) {
        contact.lastMessageContent = message.content;
        contact.lastMessageDateTime = message.creationTime;
        if (!isActiveThread) {
          contact.unreadCount = (contact.unreadCount || 0) + 1;
        }
        // Move the updated contact to the top.
        state.contacts = [
          contact,
          ...state.contacts.filter((c) => c.userId !== message.senderId),
        ];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDirectContacts.pending, (state) => {
      state.loadingContacts = true;
    });
    builder.addCase(getDirectContacts.fulfilled, (state, action) => {
      state.contacts = action.payload || [];
      state.loadingContacts = false;
    });
    builder.addCase(getDirectContacts.rejected, (state, action) => {
      state.error = action.payload;
      state.loadingContacts = false;
    });

    builder.addCase(getDirectConversation.pending, (state) => {
      state.loadingMessages = true;
    });
    builder.addCase(getDirectConversation.fulfilled, (state, action) => {
      state.activeUserId = action.payload.otherUserId;
      state.messages = action.payload.messages || [];
      state.loadingMessages = false;
      // Clear unread badge for the opened thread.
      const contact = state.contacts.find(
        (c) => c.userId === action.payload.otherUserId,
      );
      if (contact) contact.unreadCount = 0;
    });
    builder.addCase(getDirectConversation.rejected, (state, action) => {
      state.error = action.payload;
      state.loadingMessages = false;
    });

    builder.addCase(sendDirectMessage.fulfilled, (state, action) => {
      const message: DirectMessage = action.payload;
      if (!message) return;
      if (
        state.activeUserId === message.recipientId &&
        !state.messages.some((m) => m.id === message.id)
      ) {
        state.messages.push(message);
      }
    });
    builder.addCase(sendDirectMessage.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export const { setActiveUser, receiveDirectMessage } =
  communicationSlice.actions;

export default communicationSlice.reducer;
